/**
 * SIL-1377 - reading a listing payload that may be a version behind.
 *
 * ## Why this file exists
 *
 * The portal and the API are separately-deployed artifacts. Version skew is a
 * normal operating condition, not an incident: a customer deploy can land while
 * an API deploy is blocked, an API can be rolled back on its own, and CloudFront
 * can serve a new `index.html` against an old Lambda for the duration of a
 * deploy. On 2026-09-30 that produced a WHITE SCREEN on `/marketplace` - the
 * primary buying surface - because two components dereferenced fields the older
 * API does not send (`TypeError: Cannot read properties of undefined (reading
 * 'gpuModel')`, `... (reading 'tier')`).
 *
 * "Always deploy the API first" is correct and was the stated plan, and it still
 * produced the crash. It is a process, not a mechanism. This file is the
 * mechanism: every read of a field that arrived with SIL-1345 or SIL-1347 goes
 * through a function whose RETURN TYPE says the field may be absent, so the
 * compiler names the call sites instead of production naming them.
 *
 * This is deliberately NOT a client-side API version check or a compatibility
 * handshake (explicitly out of scope). It is the projection contract already in
 * force: SIL-1345 OMITS a key rather than null-filling it, precisely so the UI
 * can hide the tile. An absent BLOCK is the same contract one level up.
 *
 * ## The constraint that outranks "don't crash"
 *
 * **A degraded render must never read as a positive claim.** No Verified mark,
 * no green chip, no exact price when the data is absent. Absent evidence is the
 * neutral/unknown state.
 *
 * That is why `evidenceOf` returns `undefined` and NOT a synthesised
 * "unknown" evidence block. A default block would be a second source of truth
 * for the tier, and the SIL-1366 / SIL-1371 pair are both exactly this bug:
 * absent-or-negative data rendering as something better than it was. The
 * callers therefore OMIT the evidence-derived surfaces (the strip, the AV chip,
 * the location tile) rather than fill them, and the one predicate that grants
 * the Verified mark - `showsVerifiedMark` in `AvStateChip.tsx` - is untouched
 * and unbypassed: with no evidence block there is no `evidenceTier` and no
 * `windowVerdict`, both gating inputs are `null`, and it fails closed by design.
 *
 * `technicalDetailsOf` is the one function that DOES substitute a default,
 * because `{}` is not a claim: `ListingTechnicalDetails` already means "every
 * key optional, an absent key renders no tile", so an absent whole object and an
 * object with no keys are the same statement - "no technical details to show".
 */
import type { ListingDetail } from '../../dto/listing.js';
import type {
  AskPricing,
  AvState,
  ListingTechnicalDetails,
  PaymentTerms,
  UnverifiedEvidence,
  UnverifiedPricing,
  VerifiedEvidence,
  VerifiedPricing,
} from '../../dto/marketplace.js';

export type BuyerEvidence = VerifiedEvidence | UnverifiedEvidence;
/** SIL-1480: `AskPricing` ('ask' mode) is the same exact-price shape for
 * every viewer — see `projectAskPricing` (apps/api/src/lib/buyerProjection.ts). */
export type BuyerPricing = VerifiedPricing | UnverifiedPricing | AskPricing;

/**
 * The fields an older API omits, declared OPTIONAL - which is the honest runtime
 * shape, where `MarketplaceListing` / `ListingDetail` declare them required.
 *
 * Both of those types are assignable to this one (required satisfies optional),
 * so a caller passes its listing straight in with no cast. This interface is the
 * single place the optionality is written down; widening it here rather than at
 * ~20 call sites is what stops the next such field from being guarded in four
 * places and forgotten in a fifth.
 *
 * The field list is SIL-1345's and SIL-1347's additions, in full.
 */
export interface SkewProneListing {
  /** SIL-1345 - tiles from the SKU's `class_attributes`. */
  technicalDetails?: ListingTechnicalDetails;
  /** SIL-1347 on the marketplace row; older on the detail route. */
  evidence?: BuyerEvidence;
  /** SIL-1345 - top level on a marketplace row only. */
  avState?: AvState;
  /** Predates SIL-1345, guarded anyway: it gates the exact-price cells. */
  pricing?: BuyerPricing;
  /** SIL-1345 - `null` means "seller did not state it". */
  shipsWithinDays?: number | null;
  /** SIL-1345 - `null` means "seller did not state it". */
  paymentTerms?: PaymentTerms | null;
  /** SIL-1345 - `listing.created_at`. */
  listedAt?: string;
  /** SIL-643, listed in the survey because the card branches on it. */
  saleMechanic?: 'buy_now' | 'auction';
  /** SIL-1345 - the server's mirror of the POST /offers gate. */
  acceptingOffers?: boolean;
  /** SIL-1444 - distinct asset_class_id slugs behind the listing. */
  assetTypeIds?: string[];
  /** SIL-1480 - server-computed "Verified seller" boolean. */
  sellerVerified?: boolean;
}

/**
 * Frozen so a caller cannot mutate the shared default into a per-listing one.
 * `{}` renders no tiles, which is what "no technical details" means.
 */
const NO_TECHNICAL_DETAILS: ListingTechnicalDetails = Object.freeze({});

/**
 * Never `undefined`: the callers all want to read optional keys off it, and
 * `{}` says exactly what an absent block says. Contrast `evidenceOf`.
 */
export function technicalDetailsOf(listing: SkewProneListing): ListingTechnicalDetails {
  return listing.technicalDetails ?? NO_TECHNICAL_DETAILS;
}

/**
 * `undefined` when the API sent no evidence block - NOT a synthesised neutral
 * block. See the file header: a default block is a second source of truth for
 * the tier, and every caller must instead omit the surface it would have drawn.
 */
export function evidenceOf(listing: SkewProneListing): BuyerEvidence | undefined {
  return listing.evidence ?? undefined;
}

/**
 * `undefined` when the API sent no `avState`. The AV chip needs this to place
 * the listing in one of its three states at all, so a caller with `undefined`
 * renders NO chip rather than guessing a state.
 */
export function avStateOf(listing: SkewProneListing): AvState | undefined {
  return listing.avState ?? undefined;
}

/** `undefined` when the API sent no pricing block. No block, no price cells. */
export function pricingOf(listing: SkewProneListing): BuyerPricing | undefined {
  return listing.pricing ?? undefined;
}

/**
 * SIL-1480 - `false` when the API sent no `sellerVerified` (an older API, or
 * a non-arbitrage listing). Never renders a positive claim on absent data,
 * same "absent means neutral/unknown" posture as the rest of this file.
 */
export function sellerVerifiedOf(listing: SkewProneListing): boolean {
  return listing.sellerVerified === true;
}

/**
 * Collapses "absent" into "not stated" for a nullable scalar.
 *
 * The DTO's own convention for these is `null` = "the seller did not state it",
 * and the UI already renders that as "Not stated" / hides the tile. An absent
 * key means the API cannot speak to the field at all - which is a strictly
 * weaker statement, and "Not stated" is the correct render for both. The trap
 * this closes is the `!== null` test that every existing call site uses: it is
 * TRUE for `undefined`, so a skewed payload sailed past it and rendered
 * "Ships within undefined days".
 */
export function statedNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' ? value : null;
}

/** `statedNumber`'s sibling for the payment-terms enum. */
export function statedPaymentTerms(value: PaymentTerms | null | undefined): PaymentTerms | null {
  return value ?? null;
}

/**
 * The node count a card or detail page should show, from whichever of the two
 * sources the payload actually carries.
 *
 * Returns `0` when neither resolves, which every caller already treats as "say
 * nothing about the node count" - it never becomes a rendered `0 nodes`.
 */
export function nodeCountFromEvidence(evidence: BuyerEvidence | undefined): number {
  if (evidence === undefined) return 0;
  return evidence.tier === 'verified' ? evidence.verificationSummary.nodes : evidence.nodeCount;
}

/**
 * The node count the detail page shows, for any listing type. `quantity` is the
 * listing's own count for a sku listing; the evidence node count covers lot and
 * asset listings. Floored at 1 so the "per node" and "lot total" rows stay
 * coherent for a listing whose evidence rows are missing (SIL-1377: read through
 * `evidenceOf`, so an API one version behind cannot white-screen the route).
 */
export function nodeCountOf(listing: ListingDetail): number {
  const fromEvidence = nodeCountFromEvidence(evidenceOf(listing));
  return listing.quantity ?? (fromEvidence > 0 ? fromEvidence : 1);
}
